import React, { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { calculateTableStatus } from '../utils/tableUtils';

interface TableGridProps {
  onTableClick: (tableId: number) => void;
}

const TableGrid: React.FC<TableGridProps> = ({ onTableClick }) => {
  const { state } = useApp();
  const { tables } = state;

  // Berechne Grid-Klasse basierend auf Tischanzahl für optimale Darstellung
  const gridClass = useMemo(() => {
    const count = tables.length;
    if (count <= 16) return 'table-grid small-count';
    if (count >= 40) return 'table-grid large-count';
    return 'table-grid';
  }, [tables.length]);

  // Sortiere Tische nach Nummer für konsistente Darstellung
  const sortedTables = useMemo(() => {
    return [...tables].sort((a, b) => {
      // Sichere Handhabung von möglicherweise fehlenden number Properties
      const aNumber = a.number || a.id.toString();
      const bNumber = b.number || b.id.toString();
      
      // Versuche numerischen Vergleich, falle zurück auf String-Vergleich
      const aNum = parseInt(aNumber.replace(/\D/g, ''));
      const bNum = parseInt(bNumber.replace(/\D/g, ''));
      
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      
      return aNumber.localeCompare(bNumber);
    });
  }, [tables]);

  return (
    <div className={gridClass}>
      {sortedTables.map((table) => {
        const status = calculateTableStatus(table);
        const statusClass = getStatusColorClass(status);
        
        return (
          <div
            key={table.id}
            className={`table-card ${statusClass}`}
            onClick={() => onTableClick(table.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onTableClick(table.id);
              }
            }}
          >
            <div className="table-number">
              {table.number}
            </div>
            <div className="table-status-icon">
              {getStatusIcon(status, table)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Hilfsfunktion für CSS-Klassen
const getStatusColorClass = (status: string): string => {
  switch (status) {
    case 'available':
      return 'table-available';
    case 'cooking':
      return 'table-cooking';
    case 'confirmed':
      return 'table-confirmed';
    case 'kitchen_ready_pending':
      return 'table-kitchen-ready-pending';
    case 'partially_ready':
      return 'table-partially-ready';
    case 'ready':
      return 'table-ready';
    case 'cancelled':
      return 'table-cancelled';
    default:
      return 'table-available';
  }
};

// Hilfsfunktion für Status-Icons (minimalistisch)
const getStatusIcon = (status: string, table: any): string => {
  switch (status) {
    case 'available':
      return ''; // Keine Anzeige bei "Frei" - nur Tischnummer
    case 'cooking':
      return table.actions.pizzaConfirmed ? '🍳' : '🍕'; // Kochen (🍳) vs Küche gestartet (🍕)
    case 'confirmed':
      return '⚡'; // Beide arbeiten aktiv parallel
    case 'kitchen_ready_pending':
      return '👨‍🍳🍕'; // Küche fertig, Pizza-Bestätigung fehlt
    case 'partially_ready':
      // Zeige welche Partei fertig ist
      if (table.actions.kitchenReady && !table.actions.pizzaReady) {
        return '👨‍🍳✅'; // Küche fertig
      } else if (table.actions.pizzaReady && !table.actions.kitchenReady) {
        return '🍕✅'; // Pizza fertig
      }
      return '⚡'; // Fallback für partially ready
    case 'ready':
      return '🔔'; // Bereit zum Servieren
    case 'cancelled':
      return '❌'; // Storniert
    default:
      return '';
  }
};

export default TableGrid;